import { ChevronDown, ChevronRight } from "lucide-react";
import { slug } from "@/components/admin/adminTree";

/**
 * The admin's navigation: one dense, click-open tree over everything the owner can
 * configure — the registry domains AND the specialized modules, in the same tree,
 * because "where do I change the floor plan" and "where do I change the bill
 * footer" are the same question to the person asking it.
 *
 *     Management
 *       Menu Catalogue
 *       Printers & Routing
 *     Configuration
 *       Billing
 *         Charges                       5
 *           Service charge
 *           ▸ Advanced options          4
 *
 * Management sits above Configuration because the modules are the things an
 * owner opens on purpose -- the menu, the floor, the printers -- while the
 * registry below is where they go to change one value they were sent to find.
 *
 * Every level is a real node with a real target: a domain opens on its first
 * category rather than on a blank page, a category is the workspace page, a band
 * is the disclosure group, and a leaf is one setting. Nothing here is a mode
 * switch — the bands are progressive disclosure (§20), so an owner never picks
 * the difficulty of their own POS.
 */

const ROW = "w-full flex items-center gap-1 text-left rounded-sm transition-colors";
const IDLE = "text-muted-foreground hover:bg-muted hover:text-foreground";
const ACTIVE = "bg-muted text-primary font-medium";

function Twisty({ open }) {
  const Icon = open ? ChevronDown : ChevronRight;
  return <Icon className="w-3 h-3 shrink-0 opacity-70" aria-hidden="true" />;
}

function Count({ value }) {
  return <span className="ml-auto pl-1 text-micro tabular-nums opacity-60">{value}</span>;
}

export default function AdminNavTree({
  tree,
  modules,
  selection,
  expanded,
  onToggle,
  onOpenDomain,
  onOpenCategory,
  onOpenSetting,
  onOpenModule,
  focusKey,
}) {
  const isOpen = nodeId => expanded.has(nodeId);
  const onCategoryPage = (domain, category) =>
    selection?.kind === "category" && selection.domain === domain && selection.category === category;

  return (
    <nav
      className="w-56 shrink-0 border-r bg-muted/20 overflow-y-auto py-1.5"
      aria-label="Administration navigation"
      data-testid="admin-nav-tree"
    >
      <div className="px-2 pt-1 pb-1 text-meta font-medium text-muted-foreground">
        Management
      </div>
      {modules.map(module => (
        <button
          key={module.id}
          type="button"
          className={`${ROW} px-2 min-h-8 text-body font-medium ${
            selection?.kind === "module" && selection.id === module.id ? ACTIVE : IDLE
          }`}
          onClick={() => onOpenModule(module.id)}
          aria-current={selection?.kind === "module" && selection.id === module.id ? "page" : undefined}
          data-testid={`admin-tree-module-${module.id}`}
        >
          <span className="w-3 shrink-0" aria-hidden="true" />
          <span className="truncate">{module.title}</span>
        </button>
      ))}

      <div className="px-2 pt-3 pb-1 text-meta font-medium text-muted-foreground">
        Configuration
      </div>

      {!tree.length && (
        <p className="px-2 py-1 text-micro text-muted-foreground" data-testid="admin-nav-empty">
          Settings could not be loaded from the server. Check that the server PC is on and connected.
        </p>
      )}

      {tree.map(domain => {
        const open = isOpen(domain.nodeId);
        return (
          <div key={domain.nodeId}>
            <button
              type="button"
              className={`${ROW} px-2 min-h-8 text-body font-medium ${
                selection?.kind === "category" && selection.domain === domain.domain ? ACTIVE : IDLE
              }`}
              onClick={() => onOpenDomain(domain.domain)}
              aria-expanded={open}
              data-testid={`admin-tree-domain-${slug(domain.domain)}`}
            >
              <Twisty open={open} />
              <span className="truncate">{domain.domain}</span>
              <Count value={domain.categories.reduce((total, category) => total + category.count, 0)} />
            </button>

            {open && domain.categories.map(category => {
              const categoryOpen = isOpen(category.nodeId);
              return (
                <div key={category.nodeId}>
                  <button
                    type="button"
                    className={`${ROW} pl-5 pr-2 min-h-8 text-body ${
                      onCategoryPage(domain.domain, category.category) ? ACTIVE : IDLE
                    }`}
                    onClick={() => onOpenCategory(domain.domain, category.category)}
                    aria-expanded={categoryOpen}
                    aria-current={onCategoryPage(domain.domain, category.category) ? "page" : undefined}
                    data-testid={`admin-tree-category-${slug(domain.domain)}-${slug(category.category)}`}
                  >
                    <Twisty open={categoryOpen} />
                    <span className="truncate">{category.category}</span>
                    <Count value={category.count} />
                  </button>

                  {categoryOpen && category.bands.map(band => {
                    // The normal band has no header anywhere: its settings hang
                    // straight off the category, which is what "directly visible"
                    // means. Only advanced and expert earn a node.
                    if (band.id === "normal") {
                      return band.items.map(meta => (
                        <SettingLeaf
                          key={meta.key}
                          meta={meta}
                          indent="pl-8"
                          focused={focusKey === meta.key}
                          onOpen={() => onOpenSetting(domain.domain, category.category, meta.key)}
                        />
                      ));
                    }

                    const bandOpen = isOpen(band.nodeId);
                    return (
                      <div key={band.nodeId}>
                        <button
                          type="button"
                          className={`${ROW} pl-8 pr-2 min-h-7 text-meta font-medium ${IDLE}`}
                          onClick={() => onToggle(band.nodeId)}
                          aria-expanded={bandOpen}
                          data-testid={`admin-tree-band-${slug(domain.domain)}-${slug(category.category)}-${band.id}`}
                        >
                          <Twisty open={bandOpen} />
                          <span className="truncate">{band.label}</span>
                          <Count value={band.items.length} />
                        </button>
                        {bandOpen && band.items.map(meta => (
                          <SettingLeaf
                            key={meta.key}
                            meta={meta}
                            indent="pl-11"
                            focused={focusKey === meta.key}
                            onOpen={() => onOpenSetting(domain.domain, category.category, meta.key)}
                          />
                        ))}
                      </div>
                    );
                  })}
                </div>
              );
            })}
          </div>
        );
      })}

    </nav>
  );
}

function SettingLeaf({ meta, indent, focused, onOpen }) {
  return (
    <button
      type="button"
      className={`${ROW} ${indent} pr-2 min-h-7 text-meta ${focused ? ACTIVE : IDLE}`}
      onClick={onOpen}
      title={meta.description}
      data-testid={`admin-tree-setting-${meta.key}`}
    >
      <span className="truncate">{meta.display_name || meta.key}</span>
    </button>
  );
}
